import { Component, ElementRef, OnDestroy, OnInit, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { CookieConsentService } from '@cnt-workspace/data-access';
import { BottomNavVisibilityService } from '../bottom-nav/bottom-nav-visibility.service';

/** Auto-dismissing cookie consent banner. Renders once at app shell
 *  level and hides the moment the visitor accepts or declines. Sits
 *  above the bottom-nav (which is itself `fixed bottom-0`) by using
 *  a z-index higher than the nav, with safe-area padding so iPhone
 *  notches don't clip it. */
@Component({
  selector: 'cnt-cookie-consent',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './cookie-consent.component.html',
})
export class CookieConsentComponent implements OnInit, OnDestroy {
  private consent = inject(CookieConsentService);
  private platformId = inject(PLATFORM_ID);
  private navVisibility = inject(BottomNavVisibilityService);

  visible = false;
  /** Bottom offset so the banner doesn't sit on top of the mobile
   *  bottom-nav (its actual height + safe area, read from
   *  --cnt-bottom-nav-h). Zero whenever the nav isn't shown. */
  bottomOffset = 0;

  private sub: Subscription | null = null;
  private navSub: Subscription | null = null;
  private bannerObserver: ResizeObserver | null = null;
  private bannerEl: HTMLElement | null = null;

  /** The banner, when shown. Its covered height is published as
   *  `--cnt-cookie-banner-h` on :root so floating controls (the /search
   *  List/Map pill) can sit above it instead of under it. */
  @ViewChild('banner')
  set banner(ref: ElementRef<HTMLElement> | undefined) {
    this.bannerObserver?.disconnect();
    this.bannerObserver = null;
    if (!isPlatformBrowser(this.platformId)) return;
    this.bannerEl = ref?.nativeElement ?? null;
    if (this.bannerEl && typeof ResizeObserver !== 'undefined') {
      this.bannerObserver = new ResizeObserver(() => this.publishCoveredHeight());
      this.bannerObserver.observe(this.bannerEl);
    }
    this.publishCoveredHeight();
  }
  private resizeHandler: (() => void) | null = null;

  ngOnInit(): void {
    this.sub = this.consent.hasDecided$.subscribe(decided => {
      this.visible = !decided;
    });
    this.navSub = this.navVisibility.visible$.subscribe(() => this.updateBottomOffset());
    this.resizeHandler = () => this.updateBottomOffset();
    window.addEventListener('resize', this.resizeHandler, { passive: true });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.navSub?.unsubscribe();
    this.bannerObserver?.disconnect();
    this.bannerEl = null;
    if (isPlatformBrowser(this.platformId)) this.publishCoveredHeight();
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
  }

  /** Bottom-nav is `md:hidden` and only shows for signed-in users on routes
   *  without their own bottom bar, so we offset only then. Reads
   *  BottomNavComponent's published `--cnt-bottom-nav-h` (see that
   *  component's `nav` setter) rather than guessing the nav's height here
   *  too — it's already measured there via ResizeObserver. */
  private updateBottomOffset(): void {
    // getComputedStyle isn't implemented by Angular SSR's domino DOM shim
    // (unlike window/document themselves, which it does stub) — this must
    // stay browser-only, same as publishCoveredHeight() below.
    if (!isPlatformBrowser(this.platformId)) return;
    const navHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--cnt-bottom-nav-h')) || 0;
    this.bottomOffset = this.navVisibility.visible && window.innerWidth < 768 ? navHeight : 0;
    this.publishCoveredHeight();
  }

  /** How far up from the bottom of the screen the banner reaches; 0 when hidden. */
  private publishCoveredHeight(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    // bottomOffset (nav clearance) applies whether or not the banner itself is
    // still mounted — every other page reading --cnt-cookie-banner-h (sign-in,
    // hosting-new-listing, etc.) relies on it for nav clearance too, not just
    // the banner's own height, so it can't collapse to 0 once the banner is
    // dismissed.
    const px = this.bottomOffset + (this.bannerEl?.offsetHeight ?? 0);
    document.documentElement.style.setProperty('--cnt-cookie-banner-h', `${px}px`);
  }

  accept(): void { this.consent.accept(); }
  decline(): void { this.consent.decline(); }
}
